import { Box } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useVideoCallLayoutStyles } from './styles';
import { BetterInPortraitModal } from '../../../modals';
import { FullScreenWithoutBodyScrollLayout } from '@uplift/ui-v2';
import { useIsMobile } from '@uplift/hooks';
import { VideoCallSidebarProvider } from './VideoCallSidebar.component';


export type VideoCallLayoutProps = {
  mainParticipant: React.ReactNode;
  participants: {
    id: string;
    participant: React.ReactNode;
  }[];
  controlPanel: React.ReactNode;
  sidebar?: React.ReactNode;
};

export const VideoCallLayout: React.VFC<VideoCallLayoutProps> = (props) => {
  const { classes } = useVideoCallLayoutStyles();
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const parentContainerRef = useRef<HTMLDivElement>(null);
  const [ parentContainerWidth, setParentContainerWidth ] = useState(0);
  const isMdDown = useIsMobile('md');

  const setVideoContainerAspectRatio = useCallback(() => {
    const container = videoContainerRef.current;
    const parentContainer = parentContainerRef.current;

    if (!container || !parentContainer) return;

    const parentWidth = parentContainer.offsetWidth;
    const parentHeight = parentContainer.offsetHeight;

    const targetWidth = parentHeight * (16 / 9);
    const targetHeight = parentWidth * (9 / 16);

    if (isMdDown) {
      container.style.width = '100%';
      container.style.height = '100%';
    } else if (targetWidth > parentWidth) {
      container.style.width = `${parentWidth}px`;
      container.style.height = `${targetHeight}px`;
    } else {
      container.style.width = `${targetWidth}px`;
      container.style.height = `${parentHeight}px`;
    }
  }, [ isMdDown ]);

  useEffect(() => {
    const handleWindowResize = () => {
      setParentContainerWidth(parentContainerRef.current?.offsetWidth || 0);
    };

    setVideoContainerAspectRatio(); // Call the function initially

    window.addEventListener('resize', handleWindowResize);

    return () => {
      // Clean up the event listener when the component is unmounted
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [ setVideoContainerAspectRatio ]);

  useEffect(() => {
    const handleParentContainerResize = () => {
      setVideoContainerAspectRatio();
    };

    const observer = new ResizeObserver(handleParentContainerResize);

    observer.observe(parentContainerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [ parentContainerWidth, setVideoContainerAspectRatio ]);

  const hasSidebar = !!props.sidebar;

  return (
    <VideoCallSidebarProvider>
      <FullScreenWithoutBodyScrollLayout>
        <Box className={classes.container}>

          <BetterInPortraitModal />

          <Box
            flex={1}
            pt={{ xs: 0, sm: 0.5 }}
            pl={{ xs: 0, sm: 2 }}
            pb={2}
            pr={{ xs: 0, sm: 2, md: hasSidebar ? 0 : 2 }}
            display="flex"
            flexDirection="column"
          >
            <div
              className={classes.mainParticipantContainer}
              ref={parentContainerRef}
            >
              <div
                className={classes.mainParticipant}
                ref={videoContainerRef}
              >
                {props.mainParticipant}
                <div className={classes.participantsContainer}>
                  {props.participants.map(({ id, participant }) => {
                    return (
                      <div key={id} className={classes.participant}>
                        {participant}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={classes.controlPanelContainer}>
              {props.controlPanel}
            </div>
          </Box>
          {isMdDown ? null : props.sidebar ?? null}
        </Box>
      </FullScreenWithoutBodyScrollLayout>
    </VideoCallSidebarProvider>
  );
};
